/**
 * What a business unit carries with it.
 *
 * The same list answers both dialogs, because disabling a BU does not rewrite its children: they
 * keep their own state and merely stop being reachable. So what leaves with the BU and what comes
 * back with it is literally the same set, and one component says it either way.
 *
 * @module Pages/BUs/BUManager
 */

import { css } from '@emotion/css';
import { GLIcon } from '@group-link-one/gl-icons';
import type { IGLIcon } from '@group-link-one/gl-icons';
import { UTLoading } from '@group-link-one/gl-ui/utilities';
import type { JSX } from 'react';
import type { TenantCascadePreview } from '../BUPlatformList/BUPlatformList.types';

const listStyle = css`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-s);
`;

const itemStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  padding: var(--space-sm) var(--space-m);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-m);
  background: var(--color-surface-sunken);
`;

const countStyle = css`
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-bold);
  line-height: 1;
`;

const labelStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-secondary);
`;

const stateStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
`;

const errorStyle = css`
  font-size: var(--font-size-sm);
  color: var(--color-danger);
`;

interface CascadeEntry {
  key: keyof TenantCascadePreview;
  label: string;
  icon: IGLIcon['variant'];
}

const ENTRIES: CascadeEntry[] = [
  { key: 'pages', label: 'páginas', icon: 'Menu.SquaresFour' },
  { key: 'tours', label: 'tours', icon: 'Misc.Lightbulb' },
  { key: 'whatsNew', label: 'anúncios', icon: 'Misc.Broadcast' },
  { key: 'signatures', label: 'assinaturas', icon: 'Communication.EnvelopeSimple' },
  { key: 'useCases', label: 'use cases', icon: 'Communication.Newspaper' },
  { key: 'platformVersions', label: 'versões', icon: 'Misc.Tag' },
  { key: 'preferences', label: 'preferências salvas', icon: 'Settings.Gear' },
];

export interface CascadeSummaryProps {
  preview?: TenantCascadePreview;
  isLoading: boolean;
  error: string | null;
}

export const CascadeSummary = ({ preview, isLoading, error }: CascadeSummaryProps): JSX.Element => {
  if (isLoading) {
    return (
      <div className={stateStyle}>
        <UTLoading type='dots' />
        <span>Levantando o que está vinculado a esta BU…</span>
      </div>
    );
  }

  if (error !== null || preview === undefined) {
    return (
      <p className={errorStyle}>
        Não foi possível levantar o que está vinculado a esta BU. {error}
      </p>
    );
  }

  return (
    <div className={listStyle}>
      {ENTRIES.map(entry => (
        <div key={entry.key} className={itemStyle}>
          <GLIcon variant={entry.icon} size={16} fill='var(--color-text-muted)' />
          <span className={countStyle}>{preview[entry.key] as number}</span>
          <span className={labelStyle}>{entry.label}</span>
        </div>
      ))}
    </div>
  );
};
